import type { FileConfigDTO, LogicalFileType } from '@today/types'
import { Badge, Button } from '@today/ui-primitives'
import { FILE_TYPE_LABELS } from './file-type-labels'

/**
 * Props for {@link FileExchangeFileList}.
 *
 * @property files - The tenant's five logical files, already in the server's fixed order.
 * @property selected - The file open for editing, marked as pressed.
 * @property onSelect - Called with the file the user opens.
 */
export interface FileExchangeFileListProps {
	files: readonly FileConfigDTO[]
	selected?: LogicalFileType | null
	onSelect: (fileType: LogicalFileType) => void
}

/**
 * A tenant's logical files (TO-391 REQ-001): one row per file with its enabled
 * state and version, and an action to open it. Pure: files and the selection
 * are props.
 */
export function FileExchangeFileList({ files, selected = null, onSelect }: FileExchangeFileListProps) {
	return (
		<table className="w-full text-left text-ds-sm">
			<thead>
				<tr className="text-ds-text-muted">
					<th scope="col">File</th>
					<th scope="col">Status</th>
					<th scope="col">Version</th>
					<th scope="col">
						<span className="sr-only">Actions</span>
					</th>
				</tr>
			</thead>
			<tbody>
				{files.map((file) => {
					const label = FILE_TYPE_LABELS[file.fileType]
					return (
						<tr key={file.fileType} className="border-t border-ds-border">
							<td className="py-2 font-medium text-ds-text">{label}</td>
							<td className="py-2">
								<Badge variant={file.enabled ? 'success' : 'muted'} size="sm">
									{file.enabled ? 'Enabled' : 'Disabled'}
								</Badge>
							</td>
							<td className="py-2">{file.version === 0 ? 'Not saved yet' : `v${file.version}`}</td>
							<td className="py-2 text-right">
								<Button
									size="sm"
									variant="muted"
									aria-label={`Open ${label}`}
									aria-pressed={selected === file.fileType}
									onPress={() => onSelect(file.fileType)}
								>
									Open
								</Button>
							</td>
						</tr>
					)
				})}
			</tbody>
		</table>
	)
}
